import { useState, useEffect, ChangeEvent } from "react";
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface EarningsOverviewProps {
  userId: string;
}

function EarningsOverview({ userId }: EarningsOverviewProps) {
  // Minimum and maximum hourly rates as per requirements
  const MIN_RATE = 30;
  const MAX_RATE = 120;

  // Default values
  const [hourlyRate, setHourlyRate] = useState<number>(MIN_RATE);
  const [hoursPerWeek, setHoursPerWeek] = useState<number>(20);
  const [actualEarnings, setActualEarnings] = useState({
    total: 0,
    thisWeek: 0,
    thisMonth: 0,
    completedJobs: 0
  });
  const [loading, setLoading] = useState(true);

  // Platform fee percentage (example: 15%)
  const PLATFORM_FEE_PERCENT = 15;

  useEffect(() => {
    if (userId) {
      loadActualEarnings();
    }
  }, [userId]);

  const loadActualEarnings = async () => {
    try {
      // Get provider profile
      const { data: provider } = await supabase
        .from('provider_profiles')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (!provider) {
        setLoading(false);
        return;
      }

      // Get all completed jobs
      const { data: jobs } = await supabase
        .from('jobs')
        .select('*')
        .eq('provider_id', userId)
        .eq('status', 'completed');

      if (jobs && jobs.length > 0) {
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        let total = 0;
        let thisWeek = 0;
        let thisMonth = 0;

        jobs.forEach(job => {
          const jobDate = new Date(job.completed_at || job.updated_at);
          const amount = job.total_price || 0;
          const providerEarning = amount * 0.85; // Provider keeps 85%

          total += providerEarning;

          if (jobDate >= weekAgo) {
            thisWeek += providerEarning;
          }

          if (jobDate >= monthAgo) {
            thisMonth += providerEarning;
          }
        });

        setActualEarnings({
          total: Math.round(total),
          thisWeek: Math.round(thisWeek),
          thisMonth: Math.round(thisMonth),
          completedJobs: jobs.length
        });
      }
    } catch (error) {
      console.error('Error loading earnings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to format numbers as currency
  const formatCurrency = (value: number) =>
    `£${value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;

  // Calculations
  const grossWeekly = hourlyRate * hoursPerWeek;
  const feeWeekly = (grossWeekly * PLATFORM_FEE_PERCENT) / 100;
  const netWeekly = grossWeekly - feeWeekly;

  const grossMonthly = grossWeekly * 4; // Approximation
  const netMonthly = netWeekly * 4;

  const grossYearly = grossWeekly * 52;
  const netYearly = netWeekly * 52;

  // Handlers for the sliders / inputs
  const handleRateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (value >= MIN_RATE && value <= MAX_RATE) {
      setHourlyRate(value);
    }
  };

  const handleHoursChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (value >= 5 && value <= 60) {
      setHoursPerWeek(value);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Actual Earnings */}
      <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl shadow-lg p-6 text-white">
        <h2 className="text-2xl font-bold mb-6">Your Actual Earnings</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <p className="text-teal-100 text-sm mb-1">Total Earned</p>
            <p className="text-3xl font-bold">{formatCurrency(actualEarnings.total)}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <p className="text-teal-100 text-sm mb-1">This Week</p>
            <p className="text-3xl font-bold">{formatCurrency(actualEarnings.thisWeek)}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <p className="text-teal-100 text-sm mb-1">This Month</p>
            <p className="text-3xl font-bold">{formatCurrency(actualEarnings.thisMonth)}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <p className="text-teal-100 text-sm mb-1">Completed Jobs</p>
            <p className="text-3xl font-bold">{actualEarnings.completedJobs}</p>
          </div>
        </div>
        {actualEarnings.completedJobs === 0 && (
          <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <p className="text-sm text-teal-100">
              <i className="ri-information-line mr-2"></i>
              You haven't completed any jobs yet. Start accepting jobs to see your earnings here!
            </p>
          </div>
        )}
      </div>

      {/* Earnings Calculator */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-center">
          Calculate Potential Earnings
        </h2>

        {/* Hourly Rate Slider */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Hourly Rate (£)
          </label>
          <input
            type="range"
            min={MIN_RATE}
            max={MAX_RATE}
            step={1}
            value={hourlyRate}
            onChange={handleRateChange}
            className="w-full"
          />
          <div className="text-center mt-1">
            {formatCurrency(hourlyRate)} per hour
          </div>
        </div>

        {/* Hours Per Week Slider */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-1">
            Hours per Week
          </label>
          <input
            type="range"
            min={5}
            max={60}
            step={1}
            value={hoursPerWeek}
            onChange={handleHoursChange}
            className="w-full"
          />
          <div className="text-center mt-1">{hoursPerWeek} hrs/week</div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-1 gap-2">
          <div className="flex justify-between">
            <span>Gross Weekly:</span>
            <span className="font-medium">{formatCurrency(grossWeekly)}</span>
          </div>
          <div className="flex justify-between">
            <span>Platform Fee ({PLATFORM_FEE_PERCENT}%):</span>
            <span className="font-medium">{formatCurrency(feeWeekly)}</span>
          </div>
          <div className="flex justify-between">
            <span>Net Weekly:</span>
            <span className="font-medium text-green-600">
              {formatCurrency(netWeekly)}
            </span>
          </div>
          <hr className="my-2" />
          <div className="flex justify-between">
            <span>Gross Monthly (≈4 weeks):</span>
            <span className="font-medium">{formatCurrency(grossMonthly)}</span>
          </div>
          <div className="flex justify-between">
            <span>Net Monthly:</span>
            <span className="font-medium text-green-600">
              {formatCurrency(netMonthly)}
            </span>
          </div>
          <hr className="my-2" />
          <div className="flex justify-between">
            <span>Gross Yearly (52 weeks):</span>
            <span className="font-medium">{formatCurrency(grossYearly)}</span>
          </div>
          <div className="flex justify-between">
            <span>Net Yearly:</span>
            <span className="font-medium text-green-600">
              {formatCurrency(netYearly)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EarningsOverview;